/*
 * $Id:: ForgottenLoginForm.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState } from 'react';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import inputsMaxChars from '../../../../app/inputsMaxChars';
import { isEmail } from '../../../../common/utils/service';
import { t } from '../../../../providers';

const useStyles = makeStyles((theme) => ({
    FABoxWrapper: {
        display: 'flex',
        flexFlow: 'column nowrap',
    },
    inputWrapper: {
        marginBottom: '1.5rem',
    },
    textInput: {
        height: '2.5rem',
        width: '14rem',
        marginTop: '10px',
        '@media (max-width:600px)': {
            width: '12.5rem',
        },
    },
    inputItem: {
        marginBottom: '10px',
        flex: '1 1 auto',
    },
    gridItem: {
        flex: '1 1 auto',
    },
    inputError: {
        marginTop: '5px',
    },
    fieldLabel: {
        fontWeight: theme.typography.titleFontWeight,
        fontSize: theme.typography.titleFontSize,
        color: theme.palette.text.mainGreen,
    },
    loginButton: {
        width: '11.75rem',
        boxShadow: 'none',
        margin: '0',
        height: '2.5rem',
        padding: '0',
        '@media (max-width:600px)': {
            minWidth: '11rem',
            height: '3rem',
        },
    },
    fAInfoMessage: {
        maxWidth: '610px',
        marginBottom: '15px',
    },
    backButton: {
        width: '3.5rem',
        margin: '0',
        marginRight: '1.25rem',
    },
    resetButton: {
        minWidth: '151px',
        margin: '0',
        background: theme.palette.text.mainGreen,
        borderColor: theme.palette.text.mainGreen,
        '&:hover': {
            background: theme.palette.text.mainGreen,
            borderColor: theme.palette.text.mainGreen
        },
    },
    reCaptchaStyle: {
        fontWeight: '600',
    },
    underlinedLiteral: {
        textDecoration: 'underline',
    },
}));

const ForgottenLoginForm = ({ requestReset, goBack, loading, error }) => {
    const classes = useStyles();

    const [email, setEmail] = useState({ value: '', isValid: false, isDirty: false });

    const handleChangeValue = (event) => {
        const value = event.target.value;
        if (!inputsMaxChars.email || value.length <= inputsMaxChars.email) {
            const isValid = isEmail(value);
            setEmail({ value: event.target.value, isValid, isDirty: true });
        }
    };

    const submitEmail = (event) => {
        event.preventDefault();
        if (!email.value) return setEmail({ ...email, isDirty: true });
        if (!email.isValid) return;
        requestReset(email.value);
        return;
    };

    const reCaptcha = <span className={classes.reCaptchaStyle}>{t('reCaptcha')}</span>;
    const undelineLiteral = (literal) => <span className={classes.underlinedLiteral}>{t(literal)}</span>;
    const descriptionParams = {
        reCaptcha,
        privacyPolicy: undelineLiteral('privacy.policy'),
        serviceTerms: undelineLiteral('service.terms'),
    };

    const resetAccountForm = (
        <div className={classes.FABoxWrapper}>
            <form onSubmit={submitEmail} noValidate>
                <Grid container direction={'column'} className={classes.inputWrapper}>
                    <Grid item xs sm md className={classes.inputItem}>
                        <InputLabel role="input" htmlFor={'email'} error={email.isDirty && !email.isValid} className={classes.fieldLabel}>
                            {t('your.email')}*
                        </InputLabel>
                        <Input
                            id={'email'}
                            role="input"
                            className={classes.textInput}
                            placeholder={t('type.your.email')}
                            type={'email'}
                            name={'email'}
                            fullWidth
                            autoFocus
                            onChange={handleChangeValue}
                            value={email.value}
                            error={email.isDirty && !email.isValid}
                        />
                        {email.isDirty && !email.isValid && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t(!!email.value ? 'invalid.email.format' : 'required.field')}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item className={classes.gridItem}>
                        <Typography variant={'body2'} className={classes.fAInfoMessage}>
                            {t('forgot.password.details.description')}
                        </Typography>
                    </Grid>
                    <Grid item className={classes.gridItem}>
                        <Typography variant={'body2'} className={classes.fAInfoMessage}>
                            {t('forgot.password.site.protected.message', descriptionParams)}
                        </Typography>
                    </Grid>
                    {!!error && (
                        <Grid item className={classes.gridItem} xs sm md>
                            <Typography color={'error'}>{t(error)}</Typography>
                        </Grid>
                    )}
                </Grid>
                <Grid container spacing={0}>
                    <Grid item>
                        <Button onClick={goBack} variant={'outlined'} className={classes.backButton} type={'button'}>
                            {t('back')}
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button variant={'contained'} className={classes.resetButton} type={'submit'}>
                            {t('reset.my.password')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </div>
    );

    return loading ? <CircularProgress /> : resetAccountForm;
};

export default ForgottenLoginForm;
